import { FC, useMemo } from 'react';
import { Dialog } from 'radix-ui';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  CloseRounded,
  CountryFlag,
  Injured,
  TeamShirt,
  Typography,
  toPriceMillions,
} from '@dreamteam-monorepo/ui';
import type { Player } from '@dreamteam-monorepo/ui';
import type { MatchdayStats, FixturesData, BEPlayerDetail } from '@/lib/network';
import { fetchActiveTournament } from '@/lib/network';
import { getDisplayCompetitionName } from '@/lib/competitionLabels';
import {
  buildLockedPlayerFixtureDetails,
  buildLockedPlayerMatchStatRows,
} from './lockedPlayerInfoModalData';
import { MatchAccordion } from './MatchAccordion';

interface LockedPlayerInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  player?: Player;
  periodStats?: MatchdayStats;
  fixturesData?: FixturesData;
  periodNumber?: number | null;
  periodLabelShort?: string;
  periodLabelLong?: string;
  onSubstitute?: () => void;
  onFullProfile?: () => void;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
  captainMatchdayPoints?: number;
  lockedPlayerDetails?: BEPlayerDetail;
  /** Authoritative gameweek total (captain doubling + active booster already
   *  applied). When provided it replaces the client-side multiplier guess. */
  effectivePeriodPoints?: number | null;
  /** Explains captaincy differences between the stat rows and the effective
   *  total (e.g. "Captain (x2) +19"). */
  bonusRow?: { label: string; points: number; matches?: Record<string, string> } | null;
  activeBoosterCode?: string | null;
}

const ACTIVE_TOURNAMENT_QUERY_KEY = ['active-tournament-calendar'] as const;
const ACTIVE_TOURNAMENT_STALE_TIME_MS = 5 * 60 * 1000;

export const LockedPlayerInfoModal: FC<LockedPlayerInfoModalProps> = ({
  open,
  onOpenChange,
  player,
  periodStats,
  lockedPlayerDetails,
  fixturesData,
  periodNumber,
  periodLabelShort = 'Gameweek',
  periodLabelLong: _periodLabelLong = 'Matchday',
  onSubstitute,
  isCaptain,
  onFullProfile,
  isViceCaptain,
  captainMatchdayPoints,
  effectivePeriodPoints,
  bonusRow,
  activeBoosterCode,
}) => {
  const activeTournamentQuery = useQuery({
    queryKey: ACTIVE_TOURNAMENT_QUERY_KEY,
    queryFn: fetchActiveTournament,
    enabled: open,
    staleTime: ACTIVE_TOURNAMENT_STALE_TIME_MS,
    retry: 1,
  });

  const fixtureDetailsMultiple = useMemo(
    () =>
      [...(lockedPlayerDetails?.playerMatches ?? [])].sort((a, b) => {
        const aTime = a.kickoffAt ? new Date(a.kickoffAt).getTime() : 0;
        const bTime = b.kickoffAt ? new Date(b.kickoffAt).getTime() : 0;
        return aTime - bTime;
      }),
    [lockedPlayerDetails?.playerMatches]
  );

  // The currently playing fixture opens by default. If none is in progress, the
  // latest fixture with stats opens instead. If that has no stats either, fall
  // back to the earliest fixture with stats so the user always lands on a
  // match they can review.
  const defaultExpandedMatchIndex = useMemo(() => {
    const matchHasStats = (match: (typeof fixtureDetailsMultiple)[number]) =>
      buildLockedPlayerMatchStatRows(match, activeBoosterCode, bonusRow).length > 0;

    const playingIndex = fixtureDetailsMultiple.findIndex(
      match => match.status === 'playing' && matchHasStats(match)
    );
    if (playingIndex !== -1) return playingIndex;

    let latestIndex = -1;
    let latestTime = 0;
    fixtureDetailsMultiple.forEach((match, index) => {
      if (!matchHasStats(match)) return;
      const time = match.kickoffAt ? new Date(match.kickoffAt).getTime() : 0;
      if (time >= latestTime) {
        latestTime = time;
        latestIndex = index;
      }
    });
    if (latestIndex !== -1) return latestIndex;

    let earliestIndex = -1;
    let earliestTime = Infinity;
    fixtureDetailsMultiple.forEach((match, index) => {
      if (!matchHasStats(match)) return;
      const time = match.kickoffAt ? new Date(match.kickoffAt).getTime() : 0;
      if (time < earliestTime) {
        earliestTime = time;
        earliestIndex = index;
      }
    });
    return earliestIndex;
  }, [fixtureDetailsMultiple, activeBoosterCode, bonusRow]);

  if (!player) return null;

  const fixtureDetails = buildLockedPlayerFixtureDetails(player, fixturesData, periodNumber, {
    periodLabelShort,
  });

  // Captain multiplier logic:
  // - Captain gets 2x if they played any minutes (matchdayPoints > 0 implies played)
  // - Vice-captain gets 2x only if captain did NOT play AND vice-captain played
  const rawPoints = periodStats?.points ?? player.matchdayPoints ?? player.gameweekPoints ?? 0;
  const currentPlayerPlayed = rawPoints > 0;
  const captainDidNotPlay = (captainMatchdayPoints ?? 0) === 0;

  const multiplier = isCaptain
    ? currentPlayerPlayed
      ? 2
      : 1
    : isViceCaptain
      ? captainDidNotPlay && currentPlayerPlayed
        ? 2
        : 1
      : 1;
  const displayPoints = rawPoints * multiplier;

  // Gameweek format supplies the backend-computed effective total (captain
  // doubling and the active booster included); the multiplier guess above is
  // the matchday-era fallback.
  const periodPoints = effectivePeriodPoints ?? displayPoints;
  const competitionLabel = getDisplayCompetitionName(
    activeTournamentQuery.data?.data.competition_name,
    'Tournament'
  );
  const priceChange =
    typeof player.priceChange === 'number' && Number.isFinite(player.priceChange)
      ? player.priceChange
      : 0;
  const showPriceChange = priceChange !== 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[120] bg-[--overlay-primary]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[130] w-[390px] max-h-[90vh] md:max-h-[70vh] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[12px] bg-white shadow-[0px_4px_40px_0px_rgba(18,18,18,0.16)] focus:outline-none mt-6 md:mt-0">
          <Dialog.Title className="sr-only">Locked player details</Dialog.Title>
          <Dialog.Description className="sr-only">
            Review locked player profile and match stats.
          </Dialog.Description>
          <div className="relative flex">
            <div className="relative flex h-[100px] w-[88px] shrink-0 items-center justify-center overflow-hidden bg-[#27a550]">
              <TeamShirt
                team={player.shirtKey ?? player.country}
                position={player.position}
                assetScale="1x"
                className="absolute top-2 h-[111px] w-[111px] shrink-0"
                loading="eager"
              />
            </div>
            <div className="flex flex-1 flex-col gap-[2px] bg-[--page-header-bg-grey] px-[12px] pb-[12px] pt-[16px]">
              <Typography className="text-[14px] leading-[20px] text-[--color-neutral-900]">
                {player.firstName}
              </Typography>
              <div className="flex items-center gap-[12px]">
                <Typography className="text-[14px] leading-[20px] font-bold text-[--color-neutral-900]">
                  {player.lastName}
                </Typography>
                <span className="rounded-[4px] bg-white px-[8px] py-[2px]">
                  <Typography variant="subheadline010" className="text-[14px] leading-[18px]">
                    {player.position}
                  </Typography>
                </span>
                {Boolean(player.injuryDetails) && <Injured className="h-[16px] w-[16px]" />}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-[8px]">
                  <Typography variant="subheadline030" className="text-[18px] leading-[24px]">
                    &pound;{toPriceMillions(player.price)}
                  </Typography>
                  {showPriceChange ? (
                    <Typography
                      variant="paragraph000"
                      className="text-[12px] leading-[16px] text-[--color-neutral-700]"
                    >
                      {priceChange > 0 ? '+' : priceChange < 0 ? '-' : ''}&pound;
                      {toPriceMillions(Math.abs(priceChange))}
                    </Typography>
                  ) : null}
                </div>
                <div className="flex items-center gap-[4px]">
                  <Typography variant="paragraph000" className="text-[12px] leading-[16px] ">
                    {player.country ?? 'N/A'}
                  </Typography>
                  <div className="size-[24px] overflow-hidden rounded-full border border-white">
                    <CountryFlag
                      className="h-full w-full object-cover"
                      country={player.contestantFlagKey ?? player.country ?? 'WW'}
                    />
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close locked player modal"
              className="absolute right-[12px] top-[12px] inline-flex size-[24px] items-center justify-center text-[--color-neutral-900]"
              onClick={() => onOpenChange(false)}
            >
              <CloseRounded width={40} height={40} circleColor="transparent" crossColor="#222526" />
            </button>
          </div>

          <div className="flex flex-col gap-[16px] bg-[#f5f5f5] px-[12px] py-[16px]">
            {/* Competition */}
            <div className="flex items-center justify-between">
              <span className="rounded-[4px] bg-[--page-header-bg-grey] px-[8px] py-[4px]">
                <Typography variant="paragraph000" className="text-[14px] font-bold">
                  {fixtureDetails.periodLabel}
                </Typography>
              </span>
              <div className="flex items-center gap-[8px]">
                <Typography variant="paragraph000" className="text-[12px]">
                  Total:
                </Typography>
                <span className="rounded-[4px] bg-[#0072ee] px-[8px] py-[2px]">
                  <Typography variant="subheadline020" className="text-[14px] font-bold text-white">
                    {periodPoints} PTS
                  </Typography>
                </span>
              </div>
            </div>

            {/* Matches: each fixture carries its own stat accordion (ENG-1134). */}
            {fixtureDetailsMultiple.map((item, index) => (
              <MatchAccordion
                key={item.matchId}
                match={item}
                defaultExpanded={index === defaultExpandedMatchIndex}
                bonusRow={bonusRow}
                activeBoosterCode={activeBoosterCode}
              />
            ))}

            <div className="flex items-center gap-3">
              {onSubstitute ? (
                <Button
                  color="gold"
                  className="w-full text-[--color-neutral-900]"
                  onClick={onSubstitute}
                >
                  Substitute
                </Button>
              ) : null}
              {onFullProfile ? (
                <Button variant="outlined" color="blue" className="w-full" onClick={onFullProfile}>
                  Full Profile
                </Button>
              ) : null}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
